import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Mic, Square, Volume2 } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import {
  practiceWordOfDay,
  type WordOfDayData,
  type WordOfDayPracticeResult,
} from '../../modules/learning/api/vocabulary';
import { speakTamil } from '../../modules/learning/utils/tts';
import { startRecording } from '../../modules/learning/utils/mediaRecorder';
import { VocabularyWordImage } from './VocabularyWordImage';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  word: WordOfDayData;
  grade: number;
};

export function WordOfDayPracticeModal({ isOpen, onClose, word, grade }: Props) {
  const [recording, setRecording] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<WordOfDayPracticeResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const recorderRef = useRef<{ stop: () => void; blobPromise: Promise<Blob> } | null>(null);

  useEffect(() => {
    if (!isOpen) {
      recorderRef.current?.stop();
      setRecording(false);
      setSubmitting(false);
      setResult(null);
      setError(null);
    }
    return () => recorderRef.current?.stop();
  }, [isOpen]);

  const playWord = useCallback(() => {
    if (word.word_tts_url) void new Audio(word.word_tts_url).play();
    else speakTamil(word.word);
  }, [word]);

  const startPractice = () => {
    if (!word.flashcard_id && !word.vocabulary_entry_id) return;
    setResult(null);
    setError(null);
    playWord();
    const session = startRecording(12);
    recorderRef.current = session;
    setRecording(true);
    void session.blobPromise
      .then(async (blob) => {
        setRecording(false);
        recorderRef.current = null;
        if (!blob.size) {
          setError('No audio captured. Try again.');
          return;
        }
        setSubmitting(true);
        try {
          setResult(
            await practiceWordOfDay({
              audio: blob,
              grade,
              flashcardId: word.flashcard_id ?? undefined,
              vocabularyEntryId: word.vocabulary_entry_id ?? undefined,
            })
          );
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Validation failed');
        } finally {
          setSubmitting(false);
        }
      })
      .catch(() => {
        setRecording(false);
        setError('Microphone access failed.');
      });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Practice Today's Word" size="sm">
      <div className="space-y-5 text-center">
        <div className="flex justify-center">
          <div className="rounded-2xl bg-gradient-to-br from-indigo-50 to-violet-50 p-4 border border-indigo-100">
            <VocabularyWordImage
              flashcardId={word.flashcard_id}
              vocabularyEntryId={word.vocabulary_entry_id}
              imageUrl={word.image_url}
              hasImage={word.has_image}
              category={word.category || 'plant'}
              className="h-28 w-28 sm:h-32 sm:w-32 object-contain mx-auto"
              emojiClassName="text-6xl leading-none block"
              alt={word.meaning_en || word.word}
            />
          </div>
        </div>
        <div>
          <p className="text-3xl font-bold tamil-text text-slate-900">{word.word}</p>
          <p className="text-app-primary font-medium mt-1">{word.meaning_en}</p>
        </div>
        <p className="text-sm text-slate-500">Listen to the word, then tap Record and say it clearly.</p>
        <div className="flex justify-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={playWord} className="gap-1.5">
            <Volume2 className="w-4 h-4" />
            Listen
          </Button>
          {!recording ? (
            <Button type="button" size="sm" disabled={submitting} onClick={startPractice} className="gap-1.5">
              <Mic className="w-4 h-4" />
              {submitting ? 'Checking…' : 'Record'}
            </Button>
          ) : (
            <Button type="button" variant="outline" size="sm" onClick={() => recorderRef.current?.stop()} className="gap-1.5">
              <Square className="w-4 h-4" />
              Stop
            </Button>
          )}
        </div>
        {recording && <p className="text-sm text-rose-600 animate-pulse">Recording… speak now</p>}
        {error && <p className="text-sm text-rose-600">{error}</p>}
        {result && (
          <div
            className={`rounded-xl border p-4 text-left ${
              result.passed ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-amber-50 border-amber-200 text-amber-900'
            }`}
          >
            <p className="font-semibold text-lg">
              {result.passed ? 'Great job!' : 'Try again'} — {result.feedback_title}
            </p>
            {result.passed && result.stars_awarded > 0 && (
              <p className="text-sm font-medium mt-1">+{result.stars_awarded} stars</p>
            )}
            <p className="text-xs mt-2 tabular-nums">{Math.round(result.similarity_score)}% pronunciation match</p>
          </div>
        )}
      </div>
    </Modal>
  );
}
